import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { ServiceStatusResponse } from '../@types/service.type';
import { getCurrentUserAllocatedServices } from '../api/services';
import { 
  getServiceCheckIn,
  createServiceCheckIn,
  updateServiceCheckOut,
  getServiceListByTechnician,
  getTechnicianListByLocationNodeId,
  createBulkServiceCheckInQuery,
} from '../api/checkin-checkout';
import { ServiceTypeKey } from '../configs/service-type';
import {
  ServiceLogAllKey,
  ServiceCheckInKey,
  ServiceLinkKey,
  TechniciansKey,
  CheckInKey,
  CurrentUserAllocatedServicesKey,
  BulkCheckInKey,
  GetServiceKey
} from '../configs/services';
import { PartActionKey } from '../configs/partAction';

export const useGetServiceCheckIn = (id: number): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceCheckInKey, id],
    queryFn: () => getServiceCheckIn(id),
    enabled: false,
    select: (response: any) => response
  });
};

export const useCreateServiceCheckIn = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      return await createServiceCheckIn(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceCheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id]
      });
    }
  });
};

export const useGetServiceCheckInData = (id: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: [CheckInKey, id],
    queryFn: () => getServiceCheckIn(id)
  });
};

export const useCreateBulkServiceCheckInQuery = (): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      return await createBulkServiceCheckInQuery(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [BulkCheckInKey]
      });
    }
  });
};

export const useUpdateServiceCheckOut = (id: number, serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      return await updateServiceCheckOut(id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceCheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, serviceId]
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, serviceId]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id]
      });
    }
  });
};

export const useGetServiceListByTechnician = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: [ServiceLinkKey],
    queryFn: () => getServiceListByTechnician()
  });
};

export const useGetTechnicianListByLocationNodeId = (locationNodeId: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: [TechniciansKey, locationNodeId],
    queryFn: () => locationNodeId && getTechnicianListByLocationNodeId(locationNodeId)
  });
};

export const useGetCurrentUserAllocatedServices = (locationNodeId: number, serviceId: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: [CurrentUserAllocatedServicesKey, serviceId],
    queryFn: () => locationNodeId !== 0 && serviceId !== 0 && getCurrentUserAllocatedServices(locationNodeId, serviceId)
  });
};